'use client'

import { cn } from '@/utilities/ui'
import Link from 'next/link'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

type NavLinkProps = {
  href: string
  label: string
  isActive: boolean
  testId: string
  onClick?: () => void
  disabled?: boolean
  disabledTooltip?: string
  className?: string
}

export default function NavLink({
  href,
  label,
  isActive,
  testId,
  onClick,
  disabled = false,
  disabledTooltip,
  className: classNameProp,
}: Readonly<NavLinkProps>) {
  const className = cn(
    'text-sm text-white underline-offset-4',
    disabled && 'cursor-not-allowed opacity-50',
    !disabled && (isActive ? 'underline cursor-default' : 'hover:underline'),
    classNameProp,
  )

  if (disabled) {
    const accessibleName = disabledTooltip ? `${label}. ${disabledTooltip}` : label

    if (!disabledTooltip) {
      return (
        <span
          className={className}
          aria-disabled="true"
          aria-label={accessibleName}
          data-testid={testId}
          data-disabled="true"
        >
          {label}
        </span>
      )
    }

    return (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-disabled="true"
              aria-label={accessibleName}
              className={cn(
                className,
                'inline-flex border-0 bg-transparent p-0',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cp-bright-blue',
              )}
              data-testid={testId}
              data-disabled="true"
            >
              <span aria-hidden="true">{label}</span>
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="max-w-xs border-light-grey bg-white text-cp-dark-blue"
          >
            {disabledTooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  if (isActive) {
    return (
      <span className={className} aria-current="page" data-testid={testId}>
        {label}
      </span>
    )
  }

  return (
    <Link href={href} className={className} onClick={onClick} data-testid={testId}>
      {label}
    </Link>
  )
}
